import React, { useCallback, useEffect, useState } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import {
  useFormContext,
  useWatch,
} from '@grnhse/seedling/lib/birch/components/forms/form';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { EmptyState } from '@grnhse/seedling/lib/birch/components/empty_state';
import FingerprintSix from '@grnhse/seedling/lib/birch/illustrations/fingerprint_six';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import { withLocalePrefix, localizeNumber } from 'shared/utils/translation';
import RediscoveryFilterRow from 'talent_matching/talent_rediscovery/rediscovery_filter_row';
import LoadFiltersModal from 'talent_matching/talent_rediscovery/load_filters_modal';
import SaveFiltersModal from 'talent_search/talent_rediscovery/modals/save_filters_modal';
import Api from 'talent_search/lib/api';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import {
  APPLICATION_TYPE,
  TalentRediscoveryFilters,
  TalentSearchQueryParams,
  type SavedSearch,
  type SavedSearchGroup,
  type SavedSearchItem,
} from 'talent_search/shared/types';
import type { RediscoveryProps } from 'talent_matching/types';
import FormWatcher from 'talent_search/shared/form_watcher';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import {
  toSavedSearchQueryString,
  extractFormFieldsFromSavedSearch,
} from 'talent_search/lib/query_params';
import styled from 'styled-components';

const t = withLocalePrefix('talent_matching.rediscovery');

export const DEFAULT_DIRTY_FIELDS = ['exclude_hired', 'no_strong_no_scores'];

export function buildDefaultValues(
  userDateFormat: string
): TalentRediscoveryFilters {
  return {
    milestone: null,
    stages: [],
    rejection_reasons: [],
    exclude_by_rejection_reasons: true,
    scorecard_performance: null,
    no_strong_no_scores: false,
    exclude_hired: false,
    exclude_anonymized: true,
    last_activity_start: null,
    last_activity_end: null,
    can_have: [],
    must_have: [],
    does_not_have: [],
    referrals_only: false,
    internal_only: false,
    candidate_tags: [],
    custom_fields: [],
    schools: [],
    degrees: [],
    disciplines: [],
    application_fields: [],
    full_text: 1,
    jobs: [],
    departments: [],
    offices: [],
    job_status: 'all',
    application_type: APPLICATION_TYPE.CANDIDATE,
    prospect_pools: [],
    prospect_pool_stages: [],
    prospect_owners: [],
    sources: [],
    events: [],
    user_date_format: userDateFormat,
  };
}

const TableHeaderRow = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid ${FullPalette.GREY_E500};
  background: ${FullPalette.GREY_E700};
  gap: ${Tokens.spacing[2]};
`;

const TableHeaderCell = styled(Body)`
  flex: 0 0 auto;
  font-weight: 600;
`;

const ResultsCountRow = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0;
`;

const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 48px 24px;
`;

export type RediscoveryPageContentProps = {
  rediscoveryProps: RediscoveryProps;
  apiClient: Api;
  defaultValues: TalentRediscoveryFilters;
  filteredCount: number;
  setFilteredCount: (n: number) => void;
  loading: boolean;
  setLoading: (b: boolean) => void;
  savedFilterSet: SavedSearchItem | null;
  setSavedFilterSet: (ss: SavedSearchItem | null) => void;
};

export const RediscoveryPageContent = ({
  rediscoveryProps,
  apiClient,
  defaultValues,
  filteredCount,
  setFilteredCount,
  loading,
  setLoading,
  savedFilterSet,
  setSavedFilterSet,
}: RediscoveryPageContentProps) => {
  const {
    reset,
    setValue,
    getValues,
    control,
    formState: { dirtyFields },
  } = useFormContext();
  const userDateFormat = useWatch({ control, name: 'user_date_format' }) ?? '';

  const [loadModalOpen, openLoadModal, closeLoadModal] = useModal();
  const [saveModalOpen, openSaveModal, closeSaveModal] = useModal();
  const [savedSearchOptions, setSavedSearchOptions] = useState<
    SavedSearchGroup[]
  >([]);
  const [saveFilterNames, setSaveFilterNames] = useState<string[]>([]);

  // Match Talent Search rediscovery: Your filters from API + Greenhouse group from props (raw).
  useEffect(() => {
    if (!loadModalOpen) {
      return;
    }
    let cancelled = false;
    apiClient
      .fetchSavedSearchOptions()
      .then((options) => {
        if (cancelled) {
          return;
        }
        const yourFilters: SavedSearchItem[] = (options || []).map(
          (o: { id?: number; label?: string }) => ({
            id: o.id,
            label: o.label ?? '',
          })
        );
        const greenhouseGroup = rediscoveryProps.savedSearchOptions?.[1];
        setSavedSearchOptions([
          { text: 'Your filters', items: yourFilters },
          {
            text: greenhouseGroup?.text ?? 'Greenhouse filters',
            items: greenhouseGroup?.items ?? [],
          },
        ]);
      })
      .catch(() => {
        if (!cancelled) {
          setSavedSearchOptions([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [loadModalOpen, apiClient, rediscoveryProps.savedSearchOptions]);

  // Mirror talent_search/talent_rediscovery/filters_section.tsx onSelectedFilterChange
  const handleLoadSelect = useCallback(
    async (option: SavedSearchItem | null) => {
      setSavedFilterSet(option);
      if (!option) {
        reset(defaultValues);
        closeLoadModal();
        return;
      }

      let filters: Record<string, unknown>;
      if (!option.id) {
        const preset = option.value as
          | { value: Record<string, unknown> }
          | undefined;
        filters = preset?.value ?? ({} as Record<string, unknown>);
      } else {
        const savedSearch = await apiClient.fetchSavedSearch(option.id);
        filters = (savedSearch?.value ?? {}) as Record<string, unknown>;
      }

      const parsed = extractFormFieldsFromSavedSearch(
        filters as SavedSearch,
        userDateFormat
      ) as Record<string, unknown>;
      reset();
      Object.keys(parsed).forEach((key) => {
        setValue(key, parsed[key], { shouldDirty: true });
      });
      closeLoadModal();
    },
    [
      setSavedFilterSet,
      userDateFormat,
      reset,
      closeLoadModal,
      defaultValues,
      apiClient,
      setValue,
    ]
  );

  const handleOpenSaveModal = useCallback(async () => {
    const options = await apiClient.fetchSavedSearchOptions();
    setSaveFilterNames(
      (options || []).map((o: { label?: string }) => o.label ?? '')
    );
    openSaveModal();
  }, [apiClient, openSaveModal]);

  const handleSaveFilters = useCallback(
    async (name: string) => {
      const formValues = getValues() as TalentSearchQueryParams;
      const queryString = toSavedSearchQueryString(formValues);
      await apiClient.createSavedSearch({ name, search_params: queryString });
      closeSaveModal();
    },
    [getValues, apiClient, closeSaveModal]
  );

  const hasAnyFilters =
    Object.keys(dirtyFields).filter(
      (key) => !DEFAULT_DIRTY_FIELDS.includes(key)
    ).length > 0;

  const handleClearAll = () => {
    setSavedFilterSet(null);
    reset(defaultValues);
    setFilteredCount(rediscoveryProps.unfilteredCount);
  };

  const showInitialEmptyState = !hasAnyFilters;
  const showNoResultsEmptyState =
    hasAnyFilters && filteredCount === 0 && !loading;

  return (
    <Stack spacing={0}>
      <FormWatcher
        apiClient={apiClient}
        setFilteredCount={setFilteredCount}
        setLoading={setLoading}
        shouldCache={false}
        defaultDirtyFields={DEFAULT_DIRTY_FIELDS}
      />
      <RediscoveryFilterRow
        onClearAll={handleClearAll}
        onLoadFilters={openLoadModal}
        onSaveFilters={handleOpenSaveModal}
        milestones={rediscoveryProps.milestones as Item[]}
        apiClient={apiClient}
        currencies={rediscoveryProps.currencies}
        savedFilterSet={savedFilterSet}
      />

      <ResultsCountRow>
        <Body medium>
          {localizeNumber(filteredCount)} {'results'}
        </Body>
      </ResultsCountRow>

      <TableHeaderRow data-provides="rediscovery-table-header">
        <TableHeaderCell medium>{t('table_columns.table')}</TableHeaderCell>
      </TableHeaderRow>

      {showNoResultsEmptyState && (
        <EmptyStateWrapper>
          <EmptyState
            Illustration={FingerprintSix}
            header={t('empty_state.no_results_header')}
            body={t('empty_state.no_results_body')}
            size="md"
          >
            <TertiaryButton size="md" onClick={handleClearAll}>
              {t('empty_state.clear_all_filters')}
            </TertiaryButton>
          </EmptyState>
        </EmptyStateWrapper>
      )}

      {showInitialEmptyState && !showNoResultsEmptyState && (
        <EmptyStateWrapper>
          <EmptyState
            Illustration={FingerprintSix}
            header={t('empty_state.add_filters_header')}
            body={t('empty_state.add_filters_body')}
            size="md"
          />
        </EmptyStateWrapper>
      )}

      <LoadFiltersModal
        isOpen={loadModalOpen}
        onDismiss={closeLoadModal}
        options={savedSearchOptions}
        onSelect={handleLoadSelect}
      />
      <SaveFiltersModal
        isOpen={saveModalOpen}
        close={closeSaveModal}
        onSave={handleSaveFilters}
        filterNames={saveFilterNames}
      />
    </Stack>
  );
};
